import CyanBlur from "@/assets/images/background/cyan-blur.png";
import RedBlur from "@/assets/images/background/red-blur.png";
import { Icon } from "@/components/icon";
import { Badge } from "@/ui/badge";
import { Button } from "@/ui/button";
import { ScrollArea } from "@/ui/scroll-area";
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from "@/ui/sheet";
import { Text } from "@/ui/typography";
import { getNotifications, getUnreadCount, markAsReadAndRemove, markAllAsRead, formatTimeAgo } from "@/store/notificationStore";
import { type CSSProperties, useState, useEffect } from "react";
import { useNavigate } from "react-router";

export default function NoticeButton() {
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [unreadCount, setUnreadCount] = useState(getUnreadCount());

	// Listen for notification updates
	useEffect(() => {
		const updateNotifications = () => {
			setUnreadCount(getUnreadCount());
		};

		window.addEventListener("notifications-updated", updateNotifications);
		updateNotifications(); // Initial load

		return () => {
			window.removeEventListener("notifications-updated", updateNotifications);
		};
	}, []);

	const style: CSSProperties = {
		backdropFilter: "blur(20px)",
		backgroundImage: `url("${CyanBlur}"), url("${RedBlur}")`,
		backgroundRepeat: "no-repeat, no-repeat",
		backgroundPosition: "right top, left bottom",
		backgroundSize: "50%, 50%",
	};

	return (
		<>
			<div className="relative" onClick={() => setDrawerOpen(true)}>
				<Button variant="ghost" size="icon" className="rounded-full" onClick={() => setDrawerOpen(true)}>
					<Icon icon="solar:bell-bing-bold-duotone" size={24} />
				</Button>
				{unreadCount > 0 && (
					<Badge variant="destructive" shape="circle" className="absolute -right-2 -top-2">
						{unreadCount}
					</Badge>
				)}
			</div>
			<Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
				<SheetContent side="right" className="sm:max-w-md p-0 [&>button]:hidden flex flex-col" style={style}>
					<SheetHeader className="flex flex-row items-center justify-between p-4 h-16 shrink-0">
						<SheetTitle>Értesítések</SheetTitle>
						<Button
							variant="ghost"
							size="icon"
							className="rounded-full text-primary"
							onClick={async () => {
								await markAllAsRead();
								setUnreadCount(0);
								setDrawerOpen(false);
							}}
						>
							<Icon icon="solar:check-read-broken" size={20} />
						</Button>
					</SheetHeader>
					<div className="px-4 flex-1 overflow-hidden">
						<NoticeTab onCloseDrawer={() => setDrawerOpen(false)} />
					</div>
					<SheetFooter className="flex flex-row h-16 w-full items-center justify-center p-4 shrink-0">
						<Button className="w-full" onClick={async () => {
							await markAllAsRead();
							setUnreadCount(0);
						}}>
							Összes olvasottnak jelölése
						</Button>
					</SheetFooter>
				</SheetContent>
			</Sheet>
		</>
	);
}

function NoticeTab({ onCloseDrawer }: { onCloseDrawer: () => void }) {
	const navigate = useNavigate();
	const [realTimeNotifications, setRealTimeNotifications] = useState(getNotifications());

	// Listen for notification updates
	useEffect(() => {
		const updateNotifications = () => {
			setRealTimeNotifications(getNotifications());
		};

		window.addEventListener("notifications-updated", updateNotifications);
		updateNotifications(); // Initial load

		return () => {
			window.removeEventListener("notifications-updated", updateNotifications);
		};
	}, []);

	// Transform real-time notifications to match the existing format
	// Calculate time dynamically from createdAt
	const transformedNotifications = realTimeNotifications.map((notif) => ({
		id: notif.id,
		type: notif.type || "info",
		title: notif.title,
		message: notif.message,
		time: notif.createdAt ? formatTimeAgo(notif.createdAt) : "épp most",
		read: notif.read || false,
		isSuccess: notif.type === "success",
	}));

	const renderNotification = (notification: any) => {
		// Get icon and color based on type
		const getTypeConfig = (type: string = "info") => {
			switch (type) {
				case "success":
					return {
						icon: "solar:check-circle-bold",
						variant: "success" as const,
						color: "text-green-600",
						bgColor: "bg-green-50",
					};
				case "error":
					return {
						icon: "solar:close-circle-bold",
						variant: "destructive" as const,
						color: "text-red-600",
						bgColor: "bg-red-50",
					};
				case "info":
				default:
					return {
						icon: "solar:info-circle-bold",
						variant: "default" as const,
						color: "text-blue-600",
						bgColor: "bg-blue-50",
					};
			}
		};

		const typeConfig = getTypeConfig(notification.type);

		return (
			<div key={notification.id} className="flex items-start space-x-3 py-4 border-b border-border last:border-b-0">
				<div className="w-10 h-10 rounded-full bg-bg-neutral flex items-center justify-center shrink-0">
					<Badge shape="circle" variant={typeConfig.variant}>
						<Icon icon={typeConfig.icon} size={20} />
					</Badge>
				</div>

				<div className="flex-1 min-w-0">
					{notification.title && (
						<Text variant="subTitle2" className={typeConfig.color}>
							{notification.title}
						</Text>
					)}
					{notification.message && (
						<div className={`mt-2 p-3 rounded-lg ${typeConfig.bgColor}`}>
							<Text variant="caption" className={notification.title ? "" : typeConfig.color}>
								{notification.message}
							</Text>
						</div>
					)}
					<div className="flex items-center space-x-2 mt-2">
						<Text variant="caption" color="secondary">
							{notification.time}
						</Text>
					</div>
				</div>
			</div>
		);
	};

	return (
		<div className="w-full h-full flex flex-col">
			<ScrollArea className="h-full">
				{transformedNotifications.length === 0 ? (
					<div className="flex items-center justify-center h-full text-muted-foreground py-8">
						<Text variant="subTitle2" color="secondary">
							Még nincsenek értesítések
						</Text>
					</div>
				) : (
					<div className="space-y-0">
						{transformedNotifications.map((notification) => (
							<div
								key={notification.id}
								onClick={async () => {
									const isCalendarInvite = notification.title?.toLowerCase().includes("naptár") || notification.message?.toLowerCase().includes("naptár");
									if (!notification.read) {
										await markAsReadAndRemove(notification.id);
										setRealTimeNotifications(getNotifications());
									}
									if (isCalendarInvite) {
										onCloseDrawer();
										navigate("/calendar");
									}
								}}
								className="cursor-pointer hover:bg-accent/50 transition-colors"
							>
								{renderNotification(notification)}
							</div>
						))}
					</div>
				)}
			</ScrollArea>
		</div>
	);
}
